import Breadcrumb from '@/components/ui/Breadcrumb'
import { useAllWebPages } from '@/lib/api/hooks/web-pages'
import { useFilterLegalFoundations, useSearchLegalFoundations } from '@/lib/api/hooks/legal-foundation'
import FullPageSpinner from '@/components/ui/FullPageSpinner'
import { parseHtmlString } from '@/utils/helpers/html'
import handleDownloadFromUrl from '@/utils/download'

import Search from '@/components/ui/Search'
import FilterParamsDisplay from '@/components/ui/FilterParamsDisplay'
import FILTER_SECTIONS from '@/components/pages/AboutUs/LegalFoundation/FilterModule/FILTER_SECTIONS'
import FilterModule from '@/components/ui/FilterModule'
import { useSearchFilterParams } from '@/hooks/useSearchFilterParams'
import Pagination from '@/components/ui/Pagination'

const FALLBACK_DOCUMENT_IMAGE = 'https://res.cloudinary.com/dlwegkhme/image/upload/v1755762000/8f138df83ce1a91fffd0ceb8741da5e48d832864_1_r5spnt.png'

const formatCategoryLabel = (category?: string) => {
  if (!category) return 'Uncategorized'

  return category
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

const getDateParts = (dateValue?: string | null) => {
  if (!dateValue) {
    return { month: '', year: '', date: 'Unknown Date' }
  }

  const parsedDate = new Date(dateValue)
  if (Number.isNaN(parsedDate.getTime())) {
    return { month: '', year: '', date: 'Unknown Date' }
  }

  const month = parsedDate.toLocaleDateString('en-US', { month: 'long' })
  const year = String(parsedDate.getFullYear())

  return { month, year, date: `${month} ${year}` }
}

const LegalFoundation = () => {
  const { data, isFetching, isError, refetch } = useAllWebPages();

    const legalFoundationData = data?.data && data?.data.find((page) =>
    page.codeName?.toLowerCase() === 'legal_foundations' ||
    page.websitePageName?.toLowerCase() === 'legal foundations'
  )?.content || null;


  // ── URL-driven search / filter state ──────────────────────────────────────
  const {
    mode,
    searchTerm,
    filterState,
    page,
    size,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    apiFilters,
    getActiveFilterCount
  } = useSearchFilterParams(FILTER_SECTIONS)


    // ── default query ─────────────────────────────────────────────────
  const {
    data: defaultData,
    isFetching: isDefaultDataFetching,
    isError: isDefaultDataError,
    refetch: refetchDefaultData
  } = useFilterLegalFoundations({}, { page, size }, mode === 'default') // Only fetch default data when in default mode;

  // ── Search query ─────────────────────
  const { data: searchData, isLoading: isSearchLoading, error: searchError, refetch: refetchSearchData } = useSearchLegalFoundations(
    searchTerm,
    { page, size }
  )
 
  // ── Filter query ─────────────────────
  const { data: filterData, isLoading: isFilterLoading, error: filterError, refetch: refetchFilterData } = useFilterLegalFoundations(
    apiFilters,
    { page, size },
    mode === 'filter' // Only fetch filter data when in filter mode
  )



  // ── Select the right data source based on mode ────────────────────────────
  const documentsData = mode === 'search' ? searchData?.data?.data : mode === "filter" ? filterData?.data?.data : defaultData?.data?.data ?? []
  const isLoading   = mode === 'search' ? isSearchLoading : mode === "filter" ? isFilterLoading : isDefaultDataFetching
  const error       = mode === 'search' ? searchError     : mode === "filter" ? filterError : isDefaultDataError
  const refetchSearchFilter      = mode === 'search' ? refetchSearchData : mode === "filter" ? refetchFilterData : refetchDefaultData

  const documents = Array.isArray(documentsData) && documentsData.length > 0 ?  documentsData.map((document) => ({
    ...document,
    id: document.legalFoundationId,
    category: formatCategoryLabel(document.legalFoundationCategory),
    date: getDateParts(document.datePublished || document.createdAt).date,
    month: getDateParts(document.datePublished || document.createdAt).month,
    year: getDateParts(document.datePublished || document.createdAt).year,
    image: document.coverImageUrl ?? FALLBACK_DOCUMENT_IMAGE,
    viewUrl: document.legalFoundationUrl,
    downloadUrl: document.legalFoundationUrl
  })) : [] 
  const totalPages  = mode === 'default' ? defaultData?.data?.totalPage ?? 1 : mode === 'search' ? searchData?.data?.totalPage ?? 1 : filterData?.data?.totalPage ?? 1
  const activeFilterCount = getActiveFilterCount(FILTER_SECTIONS) 

  const hasSearchInput = searchTerm.trim().length > 0
  const hasActiveFilters = activeFilterCount > 0
  const emptyStateMessage = !hasSearchInput && !hasActiveFilters && documents.length === 0
    ? 'No Legal Foundations Uploaded'
    : 'No documents found.'

  const handleDownload = async (downloadUrl?: string, title?: string) => {
    if (downloadUrl && downloadUrl !== '#') {
      handleDownloadFromUrl({url:downloadUrl, fileName:title})
    }
  }

  const handleView = (viewUrl?: string) => {
    if (viewUrl && viewUrl !== '#') {
      // Open PDF/document in new tab for viewing
      window.open(viewUrl, '_blank', 'noopener,noreferrer')
    }
  }

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'About Us', href: '/about' },
    { label: 'Legal Foundation', href: '/about/legal-foundation', active: true }
  ]

  if (isFetching) {
    return <FullPageSpinner />;
  }

  if (isError) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Error loading page.</p>
      <button
        onClick={() => {
          refetch()
        }}
        className="px-4 py-2 bg-forestGreen-100 text-white rounded"
      >
        Retry
      </button>
    </div>;
  }

  if (!legalFoundationData) {
    return <div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
      <p>Page content not found.</p>
    </div>;
  }


  return (
    <div className="bg-white min-h-screen">
      {/* Breadcrumb */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8  ">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Hero Image */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <img
          src={legalFoundationData?.LEGAL_FOUNDATION_BANNER?.fileUrl ?? "https://res.cloudinary.com/dlwegkhme/image/upload/v1756135981/b467cbb129e3f72467993c785f41b2ffab8d0f62_1_cqe6qb.png"}
          alt="African Space Agency Headquarters Building"
          className="w-full h-64 sm:h-80 md:h-96 lg:h-[480px] object-cover"
        />
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-4xl">
          <h1 className="text-3xl md:text-4xl font-normal font-pt-serif text-gray-100 leading-tight mb-6">
            {legalFoundationData?.LEGAL_FOUNDATION?.title ?? 'Legal Foundation'}
          </h1>
          <p className="text-lg font-normal font-satoshi text-gray-100 leading-relaxed max-w-3xl">
            <span dangerouslySetInnerHTML={parseHtmlString(legalFoundationData?.LEGAL_FOUNDATION?.description ?? '')} />
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Publications Section */}
        <div className="space-y-10">
          {/* <h2 className="text-xl sm:text-2xl lg:text-3xl font-normal font-pt-serif text-gray-900 leading-loose">
            Publications
          </h2> */}

          <div className="grid grid-cols-1 gap-8">
        {/* Search + Filter Controls */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center relative gap-4 justify-between flex-wrap">
              <Search
                value={searchTerm}
                onSearch={setSearch}
                placeholder="Search events..."
              />
               <FilterModule
                    sections={FILTER_SECTIONS}
                    activeFilters={filterState}
                    onApply={setFilters}
                    activeFilterCount={activeFilterCount}
                  />
            </div>

            {/* Active filter chips — only shown in filter mode */}
            <FilterParamsDisplay
              sections={FILTER_SECTIONS}
              filterState={filterState}
              onRemove={removeFilter}
              onClearAll={clearFilters}
            />
          </div>

            {/* Main Content - Documents Grid */}
            <div className="">
              {/* Loading State */}
              {isLoading && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[1, 2, 3, 4, 5, 6].map((index) => (
                    <div key={index} className="flex flex-col gap-4 animate-pulse">
                      <div className="w-full h-64 bg-gray-200 rounded"></div>
                      <div className="flex flex-col gap-4">
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                        <div className="h-6 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {
                 error ? (
              <div className="text-center py-20">
                <p className="text-sm text-red-500">Error loading events. Please try again later.</p>
                <button onClick={() => refetchSearchFilter()} className="mt-4 px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
              </div>
            ) : null
              }

              {
                documents.length > 0 ? (  
                <>
               
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 sm:gap-8 justify-items-center">
                {documents.map((document) => (
                  <div key={document.id} className="flex flex-col items-start gap-4 w-full max-w-sm">

                    <div className="w-full  h-64 sm:h-72 mx-auto">
                      <img
                        src={document.image}
                        alt={document.title}
                        className="w-full h-full object-cover rounded"
                      />
                    </div>

                    {/* Document Info */}
                    <div className="w-full space-y-10">
                      <div className="space-y-6">
                        {/* Category and Date */}
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-normal font-satoshi text-gray-600">
                            {document.category}
                          </span>
                          <div className="w-0.5 h-4 bg-gray-600 rounded-full"></div>
                          <span className="text-lg font-normal font-satoshi text-gray-600">
                            {document.date}
                          </span>
                        </div>

                        {/* Title and Description */}
                        <div className="space-y-1">
                          <h3 className="text-lg font-semibold font-satoshi text-gray-900 leading-relaxed">
                            {document.title}
                          </h3>
                          <p className="text-lg font-normal font-satoshi text-gray-900 leading-relaxed line-clamp-4">
                            <span dangerouslySetInnerHTML={parseHtmlString(document.description)} />
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-col sm:flex-row items-start sm:items-end gap-2 w-full">
                        <button
                          onClick={() => handleDownload(document.downloadUrl, document.title)}
                          className="w-full sm:w-auto px-6 sm:px-8 py-2.5 bg-wine-100 hover:bg-wine-200 text-white font-bold font-satoshi rounded transition-colors duration-200 flex items-center justify-center gap-2"
                        >

                          Download
                        </button>
                        <button
                          onClick={() => handleView(document.viewUrl)}
                          className="w-full sm:w-auto px-4 py-2.5 text-gray-900 hover:text-wine-100 transition-colors flex items-center justify-center gap-2"
                        >

                          <span className="text-base font-semibold font-satoshi">View</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

               <Pagination
                                    currentPage={page}
                                    totalPages={totalPages}
                                    onPageChange={setPage}
                                    className="p-4"
                                  />
             </>
            ) : <div className="text-center py-12">
                  <p className="text-lg text-gray-600">{emptyStateMessage}</p>
                </div>
              }
            

            
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LegalFoundation